import BigNumber from 'bignumber.js';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { StakingPoolsActions } from '../../../actions/stakingPoolActions';
import { ERC20_ABI } from '../../../blockchain/abi/typescript/Erc_20_Abi';
import { LP_BNB_STAKING_ABI } from '../../../blockchain/abi/typescript/LP_BNB_ABI';
import { LP_STAKING_ABI_BLOCK } from '../../../blockchain/abi/typescript/LP_STAKING_BLOCK';
import { LP_TOKEN_ABI_PANCAKE } from '../../../blockchain/abi/typescript/LP_TOKEN_ABI';
import {
  AVERAGE_BLOCK_TIME,
  AVERAGE_BLOCK_TIME_BNB,
} from '../../../constants/blockchain-constants';
import { State } from '../../../reducers';
import erc20Service from '../../../services/contracts/erc20.contract';
import { LPStakingPool } from '../../../services/staking/staking.types';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { findContract } from '../helpers/StakingContractService';

interface Props {
  pool: LPStakingPool;
}

//@TODO would be good to move this all to redux with redux-thunk
export function useLpStakingData({ pool }: Props) {
  const AVE_BLK_TIME = pool.networkId === 56 ? AVERAGE_BLOCK_TIME_BNB : AVERAGE_BLOCK_TIME;
  const AMOUNT_BLOCKS_IN_DAY = (60 * 60 * 24) / AVERAGE_BLOCK_TIME;
  const {
    networkId,
    stakingContractAddress,
    stakingTokenDecimals,
    rewardTokenDecimals,
    token0Symbol,
    stakingTokenAddress,
    rewardTokenSymbol,
    token1Symbol,
    flipReserves,
  } = pool;
  const [balance, setBalance] = useState('0');
  const [balanceUsd, setBalanceUsd] = useState('');
  const [tokenBalance, setTokenBalance] = useState('0');

  const [apr, setApr] = useState('0');
  const [dailyReward, setDailyReward] = useState('0');
  const [rewardsEarned, setRewardsEarned] = useState('0');
  const [rewardBalance, setRewardBalance] = useState('0');
  const [claimUnlockTime, setClaimUnlockTime] = useState('0');
  const [unlockingTime, setUnlockingTime] = useState('0');

  const dispatch = useDispatch();

  const { walletAddress } = useSelector(({ account }: State) => ({
    walletAddress: account.address,
  }));

  const [unstakedBalance, setUnstakedBalance] = useState('0');

  const {
    setStakedBalance: setStakedTokenBalanceStore,
    setApr: setAprStore,
    setStakedBalanceUsd: setStakedBalanceUsdStore,
    setUnlockingTime: setUnlockingTimeStore,
    setRewardBalance: setRewardBalanceStore,
    setClaimUnlockTime: setClaimUnlockTimeStore,
    setEstimatedDailyReward: setDailyRewardStore,
    setEarnedReward: setEarnedRewardStore,
    setTokenBalance: setTokenBalanceStore,
    setUnstakedBalance: setUnstakedBalanceStore,
  } = bindActionCreators(StakingPoolsActions, dispatch);

  const { setLpTokenRate } = bindActionCreators(StakingPoolsActions, dispatch);

  const { USD: currencies } = useSelector(({ basic }: State) => basic.currency);
  //const currencyUsdValueRewardToken = currencies[token1Symbol];
  const currencyUsdValueToken0 = currencies[token0Symbol];
  const currencyUsdValueToken1 = currencies[token1Symbol];

  const StakingContract = findContract({
    networkId,
    abi: pool.strategyType === 'BLOCK' ? LP_STAKING_ABI_BLOCK : LP_BNB_STAKING_ABI, // this needs to be more dynamic
    contractAddress: stakingContractAddress,
  });

  const StakingTokenContract = findContract({
    networkId,
    abi: LP_TOKEN_ABI_PANCAKE, // this needs to be more dynamic
    contractAddress: stakingTokenAddress,
  });

  async function getRewardsEarned() {
    try {
      const rewardsEarned = await StakingContract.methods.earnedOf(walletAddress).call();
      const shiftedRewards = new BigNumber(rewardsEarned)
        .shiftedBy(-rewardTokenDecimals)
        .toFixed(rewardTokenDecimals);
      setRewardsEarned(shiftedRewards);
      setEarnedRewardStore({
        rewardsEarned: shiftedRewards,
        id: pool.stakingContractAddress,
        networkId,
      });

      // Calculate amount avail for withrawal
      const rewardsBalance = await StakingContract.methods.rewardOf(walletAddress).call();

      const totalWithdrawalBalance = new BigNumber(rewardsEarned)
        .plus(rewardsBalance)
        .shiftedBy(rewardTokenDecimals * -1)
        .toFixed(rewardTokenDecimals);

      setRewardBalance(totalWithdrawalBalance);
      setRewardBalanceStore({
        rewardBalance: totalWithdrawalBalance,
        id: pool.stakingContractAddress,
        networkId,
      });
    } catch (error) {
      throw error;
    }
  }

  async function getTokenBalance() {
    try {
      const accountBalance = await erc20Service.balanceOf(
        pool.networkId,
        ERC20_ABI,
        pool.stakingTokenAddress,
        walletAddress,
      );

      const scaledAccountBalance = new BigNumber(accountBalance)
        .div(Math.pow(10, pool.stakingTokenDecimals))
        .toFixed();
      setTokenBalanceStore({
        balance: scaledAccountBalance,
        id: pool.stakingContractAddress,
        networkId,
      });
      setTokenBalance(scaledAccountBalance);
    } catch (error) {
      throw error;
    }
  }

  async function getBalance() {
    try {
      const stakeBalance = await StakingContract.methods.balanceOf(walletAddress).call();
      const shiftedBalance = new BigNumber(stakeBalance)
        .shiftedBy(stakingTokenDecimals * -1)
        .toFixed();
      setBalance(shiftedBalance);

      setStakedTokenBalanceStore({
        balance: shiftedBalance,
        id: pool.stakingContractAddress,
        networkId,
      });
      if (!pool.isZen) {
        const totalSupplyScaledDown =
          (await StakingTokenContract.methods.totalSupply().call()) / 10 ** stakingTokenDecimals;
        const reserves = await StakingTokenContract.methods.getReserves().call();

        const [token0Reserves, token1Reserves] = [reserves[0], reserves[1]];

        const reservesToUse = flipReserves ? token0Reserves : token1Reserves;
        const stakingTokenSupplyScaledDown =
          pool.stakingTokenAddress === '0xA6EC2a30067D72aD1E7b017642092fe89584D050'
            ? reservesToUse / 10 ** rewardTokenDecimals
            : reservesToUse / 10 ** (flipReserves ? rewardTokenDecimals : stakingTokenDecimals);
        const stakingTokenRate =
          ((stakingTokenSupplyScaledDown *
            (!flipReserves ? currencyUsdValueToken1 : currencyUsdValueToken0 || 0)) /
            totalSupplyScaledDown) *
          2;

        setLpTokenRate({ id: pool.stakingContractAddress, rate: stakingTokenRate, networkId });

        const balanceUsd = new BigNumber(stakeBalance)
          .div(10 ** stakingTokenDecimals)
          .times(stakingTokenRate)
          .toFixed(2);

        setBalanceUsd(balanceUsd);
        setStakedBalanceUsdStore({
          balance: balanceUsd === 'NaN' ? '0' : balanceUsd,
          id: pool.stakingContractAddress,
          networkId,
        });
      } else {
        const stakeBalance = await StakingContract.methods.balanceOf(walletAddress).call();
        const zenAssetPrice =
          currencies[pool.stakingTokenSymbol] || currencies[pool.stakingTokenSymbol.toUpperCase()];
        const shiftedBalance = new BigNumber(stakeBalance)
          .div(10 ** stakingTokenDecimals)
          .times(zenAssetPrice)
          .toFixed(2);

        setBalanceUsd(shiftedBalance);
        setStakedBalanceUsdStore({
          balance: shiftedBalance,
          id: pool.stakingContractAddress,
          networkId,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  // After a user has withdrawn
  async function getUnstakedBalance() {
    try {
      const unstakedBalance = await StakingContract.methods.claimedOf(walletAddress).call();

      const shiftedBalance = new BigNumber(unstakedBalance)
        .shiftedBy(rewardTokenDecimals * -1)
        .toFixed(rewardTokenDecimals);
      setUnstakedBalance(shiftedBalance);
      setUnstakedBalanceStore({
        unstakedBalance: shiftedBalance,
        id: pool.stakingContractAddress,
        networkId,
      });

      if (BigNumberHelper.isGreaterThan(shiftedBalance, '0')) {
        const time = await StakingContract.methods.rewardUnlockingTime(walletAddress).call();
        setClaimUnlockTime(time);
        setClaimUnlockTimeStore({
          claimUnlockTime: time,
          id: pool.stakingContractAddress,
          networkId,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async function getDailyReward() {
    if (pool.strategyType === 'BLOCK') {
      await getDailyBlockReward();
    } else {
      await getDailySecondReward();
    }
  }

  async function getDailySecondReward() {
    try {
      const balance = await StakingContract.methods.balanceOf(walletAddress).call();
      const totalSupply = await StakingContract.methods.totalSupply().call();

      const decimalDifference = 18 - rewardTokenDecimals;
      const perSecondReward = await StakingContract.methods.perSecondReward().call();
      const perSecondRewardScaled = new BigNumber(perSecondReward).times(
        decimalDifference === 0 ? 1 : 10 ** decimalDifference,
      );

      const result = new BigNumber(balance)
        .div(totalSupply)
        .times(perSecondRewardScaled)
        .times(86400)
        .toFixed(0);
      setDailyRewardStore({ dailyReward: result, id: pool.stakingContractAddress, networkId });
      setDailyReward(result);
    } catch (error) {
      throw error;
    }
  }

  async function getDailyBlockReward() {
    try {
      const balance = await StakingContract.methods.balanceOf(walletAddress).call();
      const totalSupply = await StakingContract.methods.totalSupply().call();

      const decimalDifference = 18 - rewardTokenDecimals;
      const perBlockReward = await StakingContract.methods.perBlockReward().call();
      const perBlockRewardScaled = new BigNumber(perBlockReward).times(
        decimalDifference === 0 ? 1 : 10 ** decimalDifference,
      );

      const result = new BigNumber(balance)
        .div(totalSupply)
        .times(perBlockRewardScaled)
        .times(AMOUNT_BLOCKS_IN_DAY)
        .toFixed(0);

      setDailyRewardStore({ dailyReward: result, id: pool.stakingContractAddress, networkId });
      setDailyReward(result);
    } catch (error) {
      throw error;
    }
  }

  async function getApr() {
    if (pool.finished) {
      setApr('0');
      setAprStore({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
      return;
    }
    if (pool.isZen) {
      await getZenApr();
      return;
    }
    if (pool.strategyType === 'BLOCK') {
      await getAprBlockReward();
    } else {
      await getAprSecondReward();
    }
  }

  async function getZenApr() {
    try {
      const amountSecondsInYear = 31536000;
      const omUsdPrice = currencies[rewardTokenSymbol];
      const zenUsdPrice =
        currencies[pool.stakingTokenSymbol] || currencies[pool.stakingTokenSymbol.toUpperCase()];
      const secondReward = await StakingContract.methods.perSecondReward().call();
      const totalSupply = await StakingContract.methods.totalSupply().call();

      if (+totalSupply === 0) {
        setApr('0');
        setAprStore({ apr: '0', id: pool.stakingContractAddress, networkId });
        return;
      }
      const yearPercent = new BigNumber(secondReward)
        .times(omUsdPrice)
        .times(new BigNumber(amountSecondsInYear))
        .times(100)
        .div(zenUsdPrice.times(totalSupply).times(1e10))
        .toFixed(2);
      setApr(yearPercent);
      setAprStore({ apr: yearPercent, id: pool.stakingContractAddress, networkId });
    } catch (e) {
      throw e;
    }
  }
  async function getAprSecondReward() {
    try {
      const amountSecondsInYear = 31536000;

      const totalSupplyScaledDown =
        (await StakingTokenContract.methods.totalSupply().call()) / 10 ** stakingTokenDecimals;
      const reserves = await StakingTokenContract.methods.getReserves().call();
      const [token0Reserves, token1Reserves] = [reserves[0], reserves[1]];
      const reservesToUse = flipReserves ? token0Reserves : token1Reserves;

      const stakingTokenSupplyScaledDown =
        reservesToUse / 10 ** (flipReserves ? rewardTokenDecimals : stakingTokenDecimals);

      const stakingTokenPrice =
        ((stakingTokenSupplyScaledDown *
          (!flipReserves ? currencyUsdValueToken1 : currencyUsdValueToken0 || 0)) /
          totalSupplyScaledDown) *
        2;

      const decimalDifference = 18 - rewardTokenDecimals;
      const secondReward = await StakingContract.methods.perSecondReward().call();
      const secondRewardScaled = new BigNumber(secondReward).times(
        pool.stakingTokenAddress === '0xD3e5ca0AFeAE61A24Ff7a9219067e51f4BFDd8d9'
          ? 10 ** decimalDifference
          : 1,
      );

      const stakingTokenSupplyBalance = await StakingContract.methods.totalSupply().call();

      if (+stakingTokenSupplyBalance === 0) {
        setApr('0');
        setAprStore({ apr: '0', id: pool.stakingContractAddress, networkId });
        return;
      }

      const yearPercent = new BigNumber(secondRewardScaled)
        .times(!flipReserves ? currencyUsdValueToken1 : currencyUsdValueToken0)
        .div(new BigNumber(stakingTokenPrice).times(stakingTokenSupplyBalance))
        .times(new BigNumber(amountSecondsInYear))
        .times(100)
        .toFixed(2);
      setApr(yearPercent);

      setAprStore({
        apr: yearPercent === 'NaN' ? '0' : yearPercent,
        id: pool.stakingContractAddress,
        networkId,
      });
    } catch (e) {
      throw e;
    }
  }

  async function getAprBlockReward() {
    try {
      const amountSecondsInYear = 31536000;

      const totalSupplyScaledDown =
        (await StakingTokenContract.methods.totalSupply().call()) / 10 ** stakingTokenDecimals;
      const reserves = await StakingTokenContract.methods.getReserves().call();
      const [token0Reserves, token1Reserves] = [reserves[0], reserves[1]];
      const reservesToUse = flipReserves ? token0Reserves : token1Reserves;

      const stakingTokenSupplyScaledDown =
        reservesToUse / 10 ** (flipReserves ? rewardTokenDecimals : stakingTokenDecimals);

      const stakingTokenPrice =
        ((stakingTokenSupplyScaledDown *
          (!flipReserves ? currencyUsdValueToken1 : currencyUsdValueToken0 || 0)) /
          totalSupplyScaledDown) *
        2;

      const decimalDifference = 18 - rewardTokenDecimals;
      const blockReward = await StakingContract.methods.perBlockReward().call();
      const blockRewardScaled = new BigNumber(blockReward).times(
        decimalDifference === 0 ? 1 : 10 ** decimalDifference,
      );

      const stakingTokenSupplyBalance = await StakingContract.methods.totalSupply().call();

      if (+stakingTokenSupplyBalance === 0) {
        setApr('0');
        setAprStore({ apr: '0', id: pool.stakingContractAddress, networkId });
        return;
      }

      const yearPercent = new BigNumber(blockRewardScaled)
        .times(!flipReserves ? currencyUsdValueToken1 : currencyUsdValueToken0)
        .div(new BigNumber(stakingTokenPrice).times(stakingTokenSupplyBalance))
        .times(new BigNumber(amountSecondsInYear))
        .div(AVE_BLK_TIME)
        .times(100)
        .toFixed(2);

      setApr(yearPercent);
      setAprStore({ apr: yearPercent, id: pool.stakingContractAddress, networkId });
    } catch (error) {
      throw error;
    }
  }

  async function getRewardUnlockTime() {
    try {
      const time = await StakingContract.methods.getRewardUnlockingTime().call();
      setUnlockingTime(time);
      setUnlockingTimeStore({ unlockingTime: time, id: pool.stakingContractAddress, networkId });
    } catch (error) {
      throw error;
    }
  }

  return {
    balance,
    balanceUsd,
    dailyReward,
    apr,
    rewardsEarned,
    rewardBalance,
    unstakedBalance,
    tokenBalance,
    claimUnlockTime,
    unlockingTime,
    getTokenBalance,
    setRewardBalance,
    getDailyReward,
    getBalance,
    getApr,
    getRewardsEarned,
    getRewardUnlockTime,
    getUnstakedBalance,
  };
}
